/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */

import { EventEmitter } from '@vatom/sdk/core'

import logger from '../../../../logger'
import Client from '../../internal/net/Client'
import Store from '../../internal/repo/Store'

import WebSockets from './WebSockets'

// This is a wrapper around the WebSockets class, which allows for multiple sockets to be active at once.
// It matches the API of that class exactly.
export default class MultiWebSockets extends EventEmitter {
  store: Store
  client: Client
  sockets: WebSockets[]
  pingTimer?: NodeJS.Timeout
  // trigger: any;

  constructor(store: Store, client: Client) {
    super()

    this.store = store
    this.client = client
    this.sockets = []
  }

  /** This will be true if the connection is ready to send and receive messages */
  get isOpen() {
    // Return true if any of the websockets are open
    return !!this.sockets.find(s => s.isOpen)
  }

  /**
   * The connect function establishes a connection to the WebSocket.
   * @public
   * @return {Promise<MultiWebSockets>}
   */
  connect() {
    // Create sockets if needed
    if (this.sockets.length === 0) {
      // Get array of socket addresses
      let addresses = this.store.websocketAddress
      logger.info('[MultiWebSockets.connect] Creating socket connection', addresses)
      if (typeof addresses == 'string') addresses = [addresses]

      // Create sockets
      for (const address of addresses)
        this.sockets.push(new WebSockets(this.store, this.client, address))

      // Override the newly created socket's trigger() and emit() to instead trigger and emit on us
      for (const socket of this.sockets) {
        socket.trigger = this.trigger.bind(this)
        // socket.triggerEvent = this.emit.bind(this);
        // socket.emit = this.emit.bind(this);
      }

      // ping every 45s
      // if (this.pingTimer) clearInterval(this.pingTimer);

      // this.pingTimer = setInterval(this.ping.bind(this), 45000);
    }

    const promises = this.sockets.map(s => s.connect())
    const allSettledPromise = allSettled(promises)

    // Connect them all
    return allSettledPromise.then(e => {
      e.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          logger.info('[MultiWebSockets.connect] Socket connected', index)
        } else {
          logger.warn('[MultiWebSockets.connect] Socket failed to connect', index, result.reason)
        }
      })
      return this
    })
  }

  /**
   * This sends a message through the web socket
   * @param {*} cmd
   */
  sendMessage(cmd: any) {
    // Send message to all sockets
    for (const socket of this.sockets) socket.sendMessage(cmd)
  }

  /**
   * This sends a message through the web socket
   * @param {*} cmd
   */
  ping() {
    // Send message to all sockets
    for (const socket of this.sockets)
      socket.sendMessage({
        id: '1',
        version: '1',
        type: 'command',
        cmd: ''
      })
  }

  /**
   * @public
   * Forcefully closes the Web socket.
   * Note: Socket will be set to null. Auto connect will be disabled.
   */
  close() {
    // Close all sockets
    for (const socket of this.sockets) socket.close()
    this.sockets = []
  }
}

export const allSettled = <T>(promises: Array<Promise<T>>) =>
  Promise.all(
    promises.map(p =>
      p
        .then(value => ({
          status: 'fulfilled' as const,
          value
        }))
        .catch(reason => ({
          status: 'rejected' as const,
          reason
        }))
    )
  )
